import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import { StrongText, StyledFlex } from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import { RecordingIcon } from '../icons/RecordingIcon';
import { useAiAssistantRecordingGlobalState } from '../shared/useAiAssistantRecordingGlobalState';
import { useAiAssistantTranscriptionContext } from '../shared/AiAssistantTranscription.context';
import { useAudioAnalyser } from '../audio/useAudioAnalyser';
import { PauseIcon } from '../icons/PauseIcon';
import { DiscardIcon } from '../icons/DiscardIcon';

import { formatElapsedTime } from './formatElapsedTime';
import { useTickingElapsedSeconds } from './AiAssistantMainMenuTileItemRecording.hooks';
import { AiAssistantMainMenuTileItemContainer } from './AiAssistantMainMenuTileItemContainer';
import { TileItemPrimaryButton } from './TileItemPrimaryButton';

const t = translationKeys.mainMenuTileItem;

type AiAssistantMainMenuTileItemRecordingProps = {
   accumulatedSeconds: number;
   sessionStartedAt: number;
   onEndTranscriptionSession: VoidFunction;
   onDiscardTranscriptionSession: VoidFunction;
   onSummarizeTranscript: VoidFunction;
};

export const AiAssistantMainMenuTileItemRecording = (props: AiAssistantMainMenuTileItemRecordingProps): JSX.Element => {
   const theme = useTheme();
   const { pauseRecording, generateNote, reset } = useAiAssistantRecordingGlobalState();
   const { mediaStream } = useAiAssistantTranscriptionContext();
   const analyser = useAudioAnalyser(mediaStream);
   const elapsedSeconds = useTickingElapsedSeconds(props.accumulatedSeconds, props.sessionStartedAt);

   const handlePause = useCallback(() => {
      props.onEndTranscriptionSession();
      pauseRecording();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onEndTranscriptionSession, pauseRecording]);

   const handleDiscard = useCallback(() => {
      props.onDiscardTranscriptionSession();
      reset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onDiscardTranscriptionSession, reset]);

   const handleGenerateNote = useCallback(() => {
      props.onEndTranscriptionSession();
      generateNote();
      props.onSummarizeTranscript();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onEndTranscriptionSession, generateNote, props.onSummarizeTranscript]);

   return (
      <AiAssistantMainMenuTileItemContainer
         icon={<RecordingIcon analyser={analyser} />}
         backgroundColor={theme.backgroundBody}
      >
         <StyledFlex direction={'column'} gap={8} alignItems={'center'}>
            <StyledFlex direction={'column'} gap={4}>
               <StyledFlex gap={8} alignItems={'flex-end'}>
                  <DiscardIcon onClick={handleDiscard} />
                  <RecordingIcon analyser={analyser} />
                  <PauseIcon onClick={handlePause} />
               </StyledFlex>
               <StyledFlex direction={'column'} gap={4} alignItems={'center'}>
                  <StrongText testId={'elapsedTime'} color={theme.borderDarker}>
                     <Translate>{formatElapsedTime(elapsedSeconds)}</Translate>
                  </StrongText>
               </StyledFlex>
            </StyledFlex>
            <TileItemPrimaryButton label={t.generateNote} testId={'generateNote'} onClick={handleGenerateNote} />
         </StyledFlex>
      </AiAssistantMainMenuTileItemContainer>
   );
};
